import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/layouts/PublicLayout';
import { rentalService } from '../../services/rental.service';
import { useToast } from '../../components/common/Toast';
import { theme } from '../../config/theme';
import Button from '../../components/common/Button';
import { HomeModernIcon, MapPinIcon, PackageIcon } from '../../components/icons/Icons';
import { formatCurrency } from '../../utils/helpers';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';

const cardStyle = {
  background: theme.colors.white,
  borderRadius: theme.borderRadius.xl,
  overflow: 'hidden',
  boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
  border: `1px solid ${theme.colors.gray[100]}`,
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  height: '100%',
};

const Rentals = () => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    fetchRentals();
  }, []);

  const fetchRentals = async () => {
    setLoading(true);
    try {
      const res = await rentalService.list();
      setRentals(res.data?.rentals || res.rentals || []);
    } catch (err) {
      toast.error('Failed to load rental properties.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppContact = (rental) => {
    if (!rental.whatsapp_number) {
      toast.error('WhatsApp contact not available for this property.');
      return;
    }

    const message = `Hi, I'm interested in renting "${rental.title}" (₹${rental.rent_per_month}/month). Is it still available?`;
    const countryCode = '91'; // India
    const whatsappUrl = `https://wa.me/${countryCode}${rental.whatsapp_number}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <PublicLayout>
      <div style={{ backgroundColor: theme.colors.gray[50], minHeight: '100vh', padding: `${theme.spacing['3xl']} 0` }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: `0 ${theme.spacing.xl}` }}>
          
          {/* Header Section */}
          <div style={{
            textAlign: 'center',
            marginBottom: theme.spacing['3xl'],
            marginTop: '34px', // account for announcement bar
          }}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '64px',
                height: '64px',
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.primary.dark})`,
                boxShadow: `0 10px 25px ${theme.colors.primary.light}80`,
                marginBottom: theme.spacing.lg
              }}
            >
              <HomeModernIcon size={32} color={theme.colors.white} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.text.primary,
                marginBottom: theme.spacing.sm,
                letterSpacing: '-0.5px'
              }}
            >
              Discover Premium Rentals
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{
                fontSize: theme.typography.fontSize.lg,
                color: theme.colors.text.secondary,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.6
              }}
            >
              Find your perfect home, apartment, or room. Verified listings from trusted sellers.
            </motion.p>
          </div>

          {/* Grid Section */}
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: theme.spacing.xl }}>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} style={{ ...cardStyle, animation: 'pulse 1.5s infinite', backgroundColor: theme.colors.gray[100] }}>
                  <div style={{ height: '240px', backgroundColor: theme.colors.gray[200] }} />
                  <div style={{ padding: theme.spacing.lg }}>
                    <div style={{ height: '20px', backgroundColor: theme.colors.gray[200], borderRadius: '4px', marginBottom: '12px' }} />
                    <div style={{ height: '14px', backgroundColor: theme.colors.gray[200], borderRadius: '4px', marginBottom: '8px', width: '80%' }} />
                    <div style={{ height: '14px', backgroundColor: theme.colors.gray[200], borderRadius: '4px', marginBottom: '24px', width: '60%' }} />
                    <div style={{ height: '40px', backgroundColor: theme.colors.gray[200], borderRadius: theme.borderRadius.md }} />
                  </div>
                </div>
              ))}
              <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }`}</style>
            </div>
          ) : rentals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', backgroundColor: theme.colors.white, borderRadius: theme.borderRadius['2xl'], border: `1px dashed ${theme.colors.gray[300]}` }}>
              <HomeModernIcon size={48} color={theme.colors.gray[400]} />
              <h3 style={{ marginTop: theme.spacing.lg, fontSize: theme.typography.fontSize.xl, color: theme.colors.text.primary }}>No Rentals Available</h3>
              <p style={{ color: theme.colors.text.secondary, marginTop: theme.spacing.sm }}>There are currently no active rental properties listed.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: theme.spacing.xl }}>
              <AnimatePresence>
                {rentals.map((rental, index) => (
                  <motion.div
                    key={rental.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                    style={cardStyle}
                  >
                    {/* Image Area */}
                    <div style={{ height: '240px', position: 'relative', overflow: 'hidden', backgroundColor: theme.colors.gray[100] }}>
                      {rental.images && rental.images.length > 0 ? (
                        <img
                          src={rental.images[0]}
                          alt={rental.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <HomeModernIcon size={48} color={theme.colors.gray[300]} />
                        </div>
                      )}
                      <div style={{
                        position: 'absolute',
                        top: theme.spacing.sm,
                        right: theme.spacing.sm,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        padding: '4px 10px',
                        borderRadius: theme.borderRadius.full,
                        fontSize: theme.typography.fontSize.xs,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: theme.colors.primary.main,
                        boxShadow: theme.shadows.sm,
                      }}>
                        {rental.vacancies} {rental.vacancies === 1 ? 'Vacancy' : 'Vacancies'}
                      </div>
                    </div>

                    {/* Content Area */}
                    <div style={{ padding: theme.spacing.xl, display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <h3 style={{
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: theme.colors.text.primary,
                        marginBottom: theme.spacing.sm,
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {rental.title}
                      </h3>

                      <p style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.text.secondary,
                        marginBottom: theme.spacing.lg,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                      }}>
                        {rental.description || 'No description available for this property.'}
                      </p>

                      <div style={{ marginTop: 'auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing.lg, paddingTop: theme.spacing.md, borderTop: `1px solid ${theme.colors.gray[100]}` }}>
                          <div>
                            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, display: 'block' }}>Rent per month</span>
                            <span style={{ fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary.main }}>
                              {formatCurrency(rental.rent_per_month)}
                            </span>
                          </div>
                          {rental.seller?.store_name && (
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, display: 'block' }}>Listed by</span>
                              <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                                {rental.seller.store_name}
                              </span>
                            </div>
                          )}
                        </div>

                        <Button
                          variant="primary"
                          fullWidth
                          onClick={() => handleWhatsAppContact(rental)}
                          style={{
                            padding: '12px',
                            fontWeight: theme.typography.fontWeight.bold,
                            background: '#25D366',
                            border: 'none',
                            boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <span style={{ fontSize: '18px' }}>📱</span>
                          Contact on WhatsApp
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default Rentals;
